import { LetPassAccountEntity } from "domain/entity/LetPassAccount/LetPassAccountEntity";
import { LetPassAccountConstant } from "presentation/constant/LetPassAccount/LetPassAccountConstant";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useLetPassAccountVM } from "presentation/hook/LetPassAccount/useLetPassAccountVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useLetPassAccountTracked } from "presentation/store/LetPassAccount/LetPassAccountProvider";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { memo, useCallback, useState } from "react";
import { HPHButton, IconButton, Loader } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, StyledAction } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const LetPassAccountTitleBar: React.FC = () => {
    const SCREEN_CONSTANT = LetPassAccountConstant;
    const [letPassAccountState] = useLetPassAccountTracked();
    const letPassAccountVM = useLetPassAccountVM();
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit, allowUpdate, allowCreate } = anaInfoState;
    const { selectedRows, letPassAccountList, searchCriteria, isShowEditPanel } = letPassAccountState;
    const messageBarVM = useMessageBarVM();
    const [isLoading, setIsLoading] = useState(false);


    const handleSearchClick = useCallback(() => {
        letPassAccountVM.onSearchClick();
    }, [letPassAccountVM]);

    const getNeedPrintReceiptList = useCallback((rows: LetPassAccountEntity[]) => {
        if (rows.length === 0) {
            return [];
        }
        const newRows = rows.filter(entity => (entity.transactionType === 'Opening' || entity.transactionType === 'Deposit') && entity.receiptNo);
        const ids = newRows.map(ety => ety.id) ?? [];
        return ids;
    }, []);

    const handleReprintClick = useCallback(() => {
        setIsLoading(true)
        let ids: number[] = [];
        if (selectedRows.length < 1) {
            ids = getNeedPrintReceiptList(letPassAccountList);
        } else {
            ids = getNeedPrintReceiptList(selectedRows);
        }
        if (ids.length === 0) {
            setIsLoading(false)
            messageBarVM.showError("No record(s) will be printed.");
            return;
        }
        letPassAccountVM.onPrintReport(ids).then(data => {
            if (data) {
                setIsLoading(false)
                messageBarVM.showError(data);
                return;
            }
            letPassAccountVM.onSearch(searchCriteria).then((data) => {
                setIsLoading(false)
            }).catch(error => {
                setIsLoading(false)
            });
        });
    }, [getNeedPrintReceiptList, letPassAccountList, letPassAccountVM, messageBarVM, searchCriteria, selectedRows]);

    const handleEditClick = useCallback(() => {
        if (selectedRows.length !== 1) {
            messageBarVM.showError("Please select a record.");
            return;
        } else {
            const entity = selectedRows[0];
            if (entity.transactionType !== 'Deposit') {
                messageBarVM.showError("Cannot edit the " + entity.transactionType + " history.");
                return;

            }
        }

        letPassAccountVM.onEditClick(null, anaInfoState.userName, "Edit", operatingUnit);
    }, [anaInfoState.userName, letPassAccountVM, messageBarVM, selectedRows, operatingUnit]);

    const handleDeposit = useCallback(() => {
        letPassAccountVM.onEditClick(null, anaInfoState.userName, "Deposit", operatingUnit);
    }, [anaInfoState.userName, letPassAccountVM, operatingUnit]);

    const handleAccountOpen = useCallback(() => {
        letPassAccountVM.onEditClick(null, anaInfoState.userName, "Opening", operatingUnit);
    }, [anaInfoState.userName, letPassAccountVM, operatingUnit]);

    const handleAccountClosure = useCallback(() => {
        const defaultCompanyCode = letPassAccountState.dynamicOptions.companyCodeDropdownOptions.length > 0 ? letPassAccountState.dynamicOptions.companyCodeDropdownOptions[0].value : null;
        letPassAccountVM.onEditClick(defaultCompanyCode, anaInfoState.userName, "Closure", operatingUnit);
    }, [anaInfoState.userName, letPassAccountState.dynamicOptions.companyCodeDropdownOptions, letPassAccountVM, operatingUnit]);

    return <Sidebarheader style={{ width: '100%', display: "flex", alignItems: "center" }}>
        <HeaderTitle>{SCREEN_CONSTANT.TITLE}</HeaderTitle>
        {(isLoading) && <Loader Indicator="Spinner" size="Medium" />}
        <StyledAction className="tm-animated-wrapper">
            <IconButton fileName='Icon-search' disabled={isShowEditPanel} size='medium' toolTipText={'Search'} toolTipArrow={false} onClick={handleSearchClick} />
            {allowUpdate && <IconButton fileName='Icon-pen' disabled={isShowEditPanel || selectedRows.length === 0} size='medium' toolTipText={'Edit'} toolTipArrow={false} onClick={handleEditClick} />}
            {(allowCreate || allowUpdate) && <div className="add-seperator" />}
            {allowCreate && <><HPHButton disabled={isShowEditPanel} label={SCREEN_CONSTANT.DEPOSIT_BUT} size={'Small'} theme={'Secondary'} onClick={handleDeposit} />
                <HPHButton disabled={isShowEditPanel} label={SCREEN_CONSTANT.ACCOUNT_OPEN_BUT} size={'Small'} theme={'Secondary'} onClick={handleAccountOpen} />
                <HPHButton disabled={isShowEditPanel} label={SCREEN_CONSTANT.ACCOUNT_CLOSURE_BUT} size={'Small'} theme={'Secondary'} onClick={handleAccountClosure} /></>}
            {allowUpdate && <HPHButton disabled={isShowEditPanel} label={WorkspaceConstant.Common.BUTTON_REPRINT} size={'Small'} theme={'Primary'} onClick={handleReprintClick} />}
        </StyledAction>
        {letPassAccountState.forceRefresh && <></>}
    </Sidebarheader >
}

export default memo(LetPassAccountTitleBar);